import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import Loader from '../lib/components/Loader';
import moment from 'moment';
import viewUtil from '../lib/viewUtil';
import './Player.css';

const PlayerHistory = ({ playerId }) => {
    const [trades, setTrades] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [rosterMoves, setRosterMoves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                setLoading(true);
                const [tradesResponse, draftsResponse, rosterMovesResponse] = await Promise.all([
                    api.get(`/api/v1/players/${playerId}/tradeHistory`),
                    api.get(`/api/v1/players/${playerId}/draftHistory`),
                    api.get(`/api/v1/players/${playerId}/rosterMoveHistory`)
                ]);
                setTrades(tradesResponse.data);
                setDrafts(draftsResponse.data);
                setRosterMoves(rosterMovesResponse.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching player history:', err);
                setError('Failed to load player history');
            } finally {
                setLoading(false);
            }
        };

        if (playerId) {
            fetchHistory();
        }
    }, [playerId]);

    if (loading) {
        return (
            <div className="mt-3">
                <h5 className="text-white">History</h5>
                <div className="text-center py-2">
                    <small className="text-muted">Loading...</small>
                </div>
            </div>
        );
    }

    if (error) {
        return null; // Silently fail
    }

    if ((!trades || trades.length === 0) && (!drafts || drafts.length === 0) && (!rosterMoves || rosterMoves.length === 0)) {
        return null; // Don't show section if no history
    }

    // Combine and sort all events by timestamp in descending chronological order (most recent first)
    const allEvents = [
        ...trades.map(trade => ({
            type: 'trade',
            data: trade,
            time: Number(trade.time) || 0
        })),
        ...drafts.map(draft => ({
            type: 'draft',
            data: draft,
            time: Number(draft.timestamp) || new Date(draft.year, 8, 1).getTime()
        })),
        ...rosterMoves.map(rosterMove => ({
            type: 'rostermove',
            data: rosterMove,
            time: Number(rosterMove.timestamp) || 0
        }))
    ].sort((a, b) => {
        // Sort in descending order (most recent first)
        return b.time - a.time;
    });

    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return (
        <div className="mt-3">
            <h5 className="text-white mb-2">History</h5>
            <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem' }}>
                Trade, draft, and roster move data available from 2022 season onward
            </small>
            <div className="player-history-list">
                {allEvents.map((event, index) => {
                    if (event.type === 'trade') {
                        const trade = event.data;
                        return (
                            <div key={`trade-${trade.time}-${index}`} className="card bg-secondary text-white mb-2">
                                <div className="card-body p-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span className="badge bg-primary">Trade</span>
                                        <small className="text-muted">
                                            {moment(trade.time).format('MMM D, YYYY')}
                                        </small>
                                    </div>
                                    <div className="trade-details">
                                        <div className="mb-2">
                                            <strong className="d-block" style={{ fontSize: '0.875rem' }}>
                                                {trade.manager1}
                                            </strong>
                                            <small className="text-muted d-block">Received:</small>
                                            <ul className="mb-0 ps-3" style={{ fontSize: '0.75rem' }}>
                                                {trade.manager1Assets.map((asset, i) => (
                                                    <li key={i}>{asset}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <strong className="d-block" style={{ fontSize: '0.875rem' }}>
                                                {trade.manager2}
                                            </strong>
                                            <small className="text-muted d-block">Received:</small>
                                            <ul className="mb-0 ps-3" style={{ fontSize: '0.75rem' }}>
                                                {trade.manager2Assets.map((asset, i) => (
                                                    <li key={i}>{asset}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    } else if (event.type === 'draft') {
                        const draft = event.data;
                        return (
                            <div key={`draft-${draft.year}-${index}`} className="card bg-secondary text-white mb-2">
                                <div className="card-body p-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span className="badge bg-success">Draft</span>
                                        <small className="text-muted">
                                            {draft.year}
                                        </small>
                                    </div>
                                    <div style={{ fontSize: '0.875rem' }}>
                                        <strong>{draft.manager}</strong> selected with the{' '}
                                        <strong>{getOrdinal(draft.pick)}</strong> pick
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            Round {draft.round}, Pick {draft.pickInRound}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    } else {
                        const rosterMove = event.data;
                        const isAdd = rosterMove.action === 'add';
                        return (
                            <div key={`rostermove-${rosterMove.transaction_key}-${rosterMove.action}-${index}`} className="card bg-secondary text-white mb-2">
                                <div className="card-body p-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span className="badge bg-warning text-dark">Roster Move</span>
                                        <small className="text-muted">
                                            {moment(Number(rosterMove.timestamp)).format('MMM D, YYYY')}
                                        </small>
                                    </div>
                                    <div style={{ fontSize: '0.875rem' }}>
                                        <strong>{rosterMove.manager}</strong> {isAdd ? 'added' : 'dropped'} this player
                                        {isAdd && rosterMove.faab_bid != null && (
                                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                FAAB: ${rosterMove.faab_bid}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
};

const PlayerPage = () => {
    const { player_id } = useParams();
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPlayer = async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/api/v1/players/${player_id}`);
                setPlayer(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching player:', err);
                setError('Failed to load player data');
            } finally {
                setLoading(false);
            }
        };

        if (player_id) {
            fetchPlayer();
        }
    }, [player_id]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!player) {
        return (
            <div className="alert alert-warning" role="alert">
                Player not found
            </div>
        );
    }

    return (
        <div className="player-page">
            <div className="card bg-dark text-white border-secondary">
                <div className="card-header bg-secondary">
                    <h3 className="mb-0">{player.name}</h3>
                    <small className="text-muted">{player.posname} | {player.nflteam}</small>
                </div>
                <div className="card-body">
                    <div className="row g-2 mb-2">
                        <div className="col-6">
                            <div className="p-2 bg-secondary rounded">
                                <small className="text-muted d-block">Manager</small>
                                <span>{player.manager || 'Free Agent'}</span>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="p-2 bg-secondary rounded">
                                <small className="text-muted d-block">Contract</small>
                                <span>
                                    {player.contract !== null && player.contract !== undefined 
                                        ? `${player.contract} year${player.contract !== 1 ? 's' : ''}`
                                        : 'N/A'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                    {player.yahooleaguerank && (
                        <div className="row g-2 mb-2">
                            <div className="col-12">
                                <div className="p-2 bg-secondary rounded">
                                    <small className="text-muted d-block">Yahoo League Rank</small>
                                    <span>{player.yahooleaguerank}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    {player.birthdate && (
                        <div className="row g-2 mb-2">
                            <div className="col-12">
                                <div className="p-2 bg-secondary rounded">
                                    <small className="text-muted d-block">Age</small>
                                    <span>{viewUtil.getAge(player.birthdate)} · {moment(player.birthdate).format('MMM D, YYYY')}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    {player.franchisetag && (
                        <div className="alert alert-info mb-2 py-2">
                            <small><strong>Franchise Tag:</strong> {player.franchisetag}</small>
                        </div>
                    )}
                    {player.injured && (
                        <div className="alert alert-danger mb-2 py-2">
                            <small><strong>Status:</strong> Injured</small>
                        </div>
                    )}
                    <div className="mt-3">
                        <a 
                            className="btn btn-sm btn-outline-primary w-100" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            href={`https://sports.yahoo.com/nfl/players/${player.id}`}
                        >
                            View on Yahoo Sports
                        </a>
                    </div>
                    <PlayerHistory playerId={player_id} />
                </div>
            </div>
        </div>
    );
};

export default PlayerPage;
